import axios from "axios";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  CalendarCheck,
  ChevronRight,
  Clock,
  XCircle,
} from "react-bootstrap-icons";

import PageHeader from "../../components/PageHeader";
import LoadingSpinner from "../../components/LoadingSpinner";
import { ApiConnectionString } from "../../constants/ApiConntection";
import { useAuthContext } from "../../hooks/useAuthContext";

import "./AppointmentDetailsPage.css";

export default function AppointmentDetailsPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuthContext();

  const [appointment, setAppointment] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (user?.token) {
      fetchData();
    }
  }, [user, id]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      setErr("");

      const response = await axios.get(
        `${ApiConnectionString}/api/client/appointments`,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );

      const foundAppointment = response.data.find(
        (x) => String(x.id || x.Id) === String(id)
      );

      if (!foundAppointment) {
        setErr("This appointment could not be found.");
        return;
      }

      setAppointment(foundAppointment);
    } catch (error) {
      console.error(error);
      setErr("Failed to load appointment.");
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";

    return new Date(dateString).toLocaleDateString("en-GB", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return "";

    return new Date(dateString).toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getDuration = (start, finish) => {
    if (!start || !finish) return "";

    const startDate = new Date(start);
    const finishDate = new Date(finish);
    const minutes = Math.round((finishDate - startDate) / 60000);

    return `${minutes} minute session`;
  };

  if (isLoading) {
    return (
      <div className="appointment-details-loading">
        <LoadingSpinner />
      </div>
    );
  }

  if (err || !appointment) {
    return (
      <div className="appointment-details-page">
        <button
          type="button"
          className="appointment-details-back"
          onClick={() => navigate("/appointments")}
        >
          ‹ Back to appointments
        </button>

        <div className="appointment-details-error-card">
          <XCircle />

          <h1>Appointment not found</h1>

          <p>{err || "This appointment could not be found."}</p>

          <button
            type="button"
            className="appointment-details-primary-btn"
            onClick={() => navigate("/appointments")}
          >
            View appointments
          </button>
        </div>
      </div>
    );
  }

  const start = appointment.start || appointment.Start;
  const finish = appointment.finish || appointment.Finish;

  return (
    <div className="appointment-details-page">
      <button
        type="button"
        className="appointment-details-back"
        onClick={() => navigate("/appointments")}
      >
        ‹ Back to appointments
      </button>

      <PageHeader
        title="Appointment details"
        text="View your therapy appointment details."
      />

      <section className="appointment-details-card">
        <div className="appointment-details-top">
          <div className="appointment-details-icon">
            <CalendarCheck />
          </div>

          <div>
            <span className="appointment-details-status">Confirmed</span>

            <h2>Therapy session</h2>

            <p>Your appointment has been booked by your therapist.</p>
          </div>
        </div>

        <div className="appointment-details-list">
          <div className="appointment-details-row">
            <CalendarCheck />

            <div>
              <span>Date</span>
              <strong>{formatDate(start)}</strong>
            </div>
          </div>

          <div className="appointment-details-row">
            <Clock />

            <div>
              <span>Time</span>
              <strong>
                {formatTime(start)} – {formatTime(finish)}
              </strong>
            </div>
          </div>

          <div className="appointment-details-row">
            <ChevronRight />

            <div>
              <span>Length</span>
              <strong>{getDuration(start, finish)}</strong>
            </div>
          </div>
        </div>

        <div className="appointment-details-note">
          <strong>Need to change this appointment?</strong>

          <p>
            Please contact your therapist directly if you need to cancel or
            rearrange this session.
          </p>
        </div>
      </section>
    </div>
  );
}